import iconProcessing from '@/assets/images/status-icons/status-processing.svg'
import iconPending    from '@/assets/images/status-icons/status-pending.svg'
import iconShipped    from '@/assets/images/status-icons/status-shipped.svg'
import iconCompleted  from '@/assets/images/status-icons/status-completed.svg'
import iconDisputed   from '@/assets/images/status-icons/exclamation-disputed.svg'
import iconRefunded   from '@/assets/images/status-icons/status-refunded.svg'
import iconCanceled   from '@/assets/images/status-icons/status-canceled.svg'
// Import directly from the sibling module — NOT the `@/constants` barrel —
// to avoid a circular import (the barrel re-exports this file), which causes a
// "Cannot access 'FILTER_MARKETPLACES' before initialization" TDZ error.
import { FILTER_MARKETPLACES, FILTER_MARKETPLACE_VALUES } from './marketplaces'

// Shared constants for the Orders feature (page, filters, and detail panel).
// Anything that's hard-coded into UI option lists, used to seed/validate URL
// query params, or maps a column field to an API sort key lives here so the
// view and its filter components stay in sync without duplicate literals.

// ─── Default filter values ───────────────────────────────────────────────────
export const DEFAULT_DATE_RANGE  = 'last_30_days'
export const DEFAULT_SORT_PRESET = 'newest'
export const DEFAULT_PAGE        = 1
export const DEFAULT_PER_PAGE    = 25

// ─── Date range presets ──────────────────────────────────────────────────────
// `short` is what we show on mobile (< md) where the trigger button is tight;
// `label` is the dropdown row text and the desktop button copy.
// `availableFrom` (YYYY-MM-DD) gates a preset until that date — used to hide a
// range that would show misleadingly incomplete data until enough history
// accrues. eBay order ingestion starts 2026-04-15, so "Last 90 days" is hidden
// until ~90 days of coverage exists (2026-07-16). Auto-appears on that date.
export const DATE_RANGE_PRESETS = [
  { value: 'yesterday',    label: 'Yesterday',    short: 'Yesterday' },
  { value: 'last_30_days', label: 'Last 30 days', short: 'Last 30d'  },
  { value: 'last_60_days', label: 'Last 60 days', short: 'Last 60d'  },
  { value: 'last_90_days', label: 'Last 90 days', short: 'Last 90d', availableFrom: '2026-07-16' },
]

export const DATE_RANGE_VALUES = DATE_RANGE_PRESETS.map(p => p.value)

// ─── Marketplaces ────────────────────────────────────────────────────────────
// Single source for the filter order lives in `constants/marketplaces.js`
// (`FILTER_MARKETPLACES`) so Orders + Sale Pick List stay consistent. Re-exported
// here under the Orders-feature names the existing consumers already import.
export const ORDER_MARKETPLACES = FILTER_MARKETPLACES

export const ORDER_MARKETPLACE_VALUES = FILTER_MARKETPLACE_VALUES

// API also accepts `other` (catch-all for non-listed marketplaces) — kept
// for URL-restore validation but not rendered as a filter option.
export const API_MARKETPLACE_VALUES = [...ORDER_MARKETPLACE_VALUES, 'other']

// ─── Toolbar sort presets ────────────────────────────────────────────────────
export const ORDER_SORT_PRESETS = [
  { value: 'newest', label: 'Newest first', icon: 'heroicons:bars-arrow-down' },
  { value: 'oldest', label: 'Oldest first', icon: 'heroicons:bars-arrow-up'   },
]

export const ORDER_SORT_PRESET_VALUES = ORDER_SORT_PRESETS.map(p => p.value)

// ─── "Contains" (item-count) filter ──────────────────────────────────────────
// Single-choice filter: isolate single-item orders from multi-item orders.
// `all` is the no-filter sentinel (omit the param); the API accepts the token
// verbatim on list + summary + chart as `contains=1` / `contains=2+`. Count is
// distinct line items, not total quantity (BE-side). See CDP-2437.
export const DEFAULT_CONTAINS = 'all'

export const ORDER_CONTAINS_PRESETS = [
  { value: 'all', label: 'All orders', short: 'Contains',      icon: 'heroicons:rectangle-stack'   },
  { value: '1',   label: '1 item',     short: '1 item',        icon: 'heroicons:document'          },
  { value: '2+',  label: '2+ items',   short: '2+ items',      icon: 'heroicons:document-duplicate' },
]

export const ORDER_CONTAINS_VALUES = ORDER_CONTAINS_PRESETS.map(p => p.value)

// ─── API sort keys ───────────────────────────────────────────────────────────
// The four values `/sales-orders` accepts on `sort`. Anything else is dropped
// before sending and the toolbar preset takes over.
export const API_SORT_KEYS = ['sold_at', 'quantity', 'gross_amount', 'picked_at']

// AG Grid column field → API `sort` value. Columns absent from this map don't
// trigger a server sort (the toolbar preset stays in effect).
export const COLUMN_TO_API_SORT = {
  sold_at:             'sold_at',
  unit_price:          'gross_amount',
  'totals.total_cost': 'gross_amount',
}

// ─── Order status pill metadata ─────────────────────────────────────────────
// Used by `OrdersStatusCell` (table) and `OrderDetailOverview` (panel) so the
// label + colour + icon for any status string only needs to change in one
// place. Backend normalises common statuses to spaces (e.g. "Ready to Ship");
// the keys here are lowercase + underscore so `resolveOrderStatus` can do a
// single transformation. British "cancelled" / US "canceled" both map.
export const ORDER_STATUS_MAP = {
  processing:    { label: 'Processing',    img: iconPending,    variant: 'gray'   },
  ready_to_ship: { label: 'Ready to Ship', img: iconProcessing, variant: 'yellow' },
  // "Sold" mirrors "Ready to Ship" visually — same icon + yellow variant. Real
  // BE data started returning this status alongside the documented ones.
  sold:          { label: 'Sold',          img: iconProcessing, variant: 'yellow' },
  shipped:       { label: 'Shipped',       img: iconShipped,    variant: 'blue'   },
  completed:     { label: 'Completed',     img: iconCompleted,  variant: 'green'  },
  disputed:      { label: 'Disputed',      img: iconDisputed,   variant: 'red'    },
  refunded:      { label: 'Refunded',      img: iconRefunded,   variant: 'pink'   },
  canceled:      { label: 'Canceled',      img: iconCanceled,   variant: 'muted'  },
  cancelled:     { label: 'Cancelled',     img: iconCanceled,   variant: 'muted'  },
}

// Unknown statuses still get a neutral pending icon so the pill never renders
// icon-less in real data — only the raw label changes.
export const ORDER_STATUS_FALLBACK = { label: '—', img: iconPending, variant: 'gray' }

// ─── Order Status filter (multi-select) ──────────────────────────────────────
// The `status` values the `/sales-orders` list accepts (comma = OR, e.g.
// `status=processing,ready_to_ship`). Keys + labels are DERIVED from
// `ORDER_STATUS_MAP` above — the single source of truth for status copy already
// used by the table pills — so the filter always shows exactly the app's
// statuses and never drifts. `canceled` (US) is dropped as it's a display alias
// of `cancelled` (the spelling the BE param + table use); Cancelled ≠ Refunded
// stay separate. This is the single source (same pattern as the marketplace
// filter + FILTER_MARKETPLACES) — the filter reads it directly, not the BE.
const ORDER_STATUS_ALIASES = ['canceled']

export const ORDER_STATUS_FILTER_KEYS = Object.keys(ORDER_STATUS_MAP)
  .filter(key => !ORDER_STATUS_ALIASES.includes(key))

export const ORDER_STATUS_FILTER_OPTIONS = ORDER_STATUS_FILTER_KEYS.map(value => ({
  value,
  label: ORDER_STATUS_MAP[value].label,
}))

export const ORDER_STATUS_FILTER_VALUES = ORDER_STATUS_FILTER_KEYS

// ─── Chart series (Orders by Marketplace) ───────────────────────────────────
// `color` is a raw hex string because Unovis renders SVG and needs a literal
// — Tailwind classes can't be applied to a `fill`/`stroke` attribute. Kept
// here (vs. inline in the view) so a future palette swap is one edit, and so
// the values can be reused by any other chart consumer that displays the
// same per-marketplace breakdown. Hex equivalents follow Tailwind's palette:
//
//   collx   → tint-1     (#0086FF) — brand blue
//   ebay    → orange-400 (#FB923C)
//   shopify → green-500  (#22C55E)
//   whatnot → gray-900   (#111827)
//   other   → purple-500 (#A855F7) — catch-all for non-listed marketplaces
export const ORDERS_CHART_SERIES = [
  { key: 'collx',   label: 'CollX',   color: '#0086FF' },
  { key: 'ebay',    label: 'eBay',    color: '#FB923C' },
  { key: 'shopify', label: 'Shopify', color: '#22C55E' },
  { key: 'whatnot', label: 'Whatnot', color: '#111827' },
  { key: 'other',   label: 'Other',   color: '#A855F7' },
]
